import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import EmojiPicker from 'emoji-picker-react';
import { Send } from 'lucide-react';

/**
 * Chat input field module. Fixed at the bottom of the screen.
 * Supports @username mention autocomplete and /kuiskaa present-user autocomplete.
 */
function getDisplayName(p) {
  return (p.displayName || p.username || '').trim() || '?';
}

function shortenPreview(text, maxLen = 50) {
  if (typeof text !== 'string') return '';
  const t = text.trim();
  return t.length <= maxLen ? t : t.slice(0, maxLen) + '...';
}

function ChatInput({
  inputRef,
  value,
  onChangeValue,
  onSubmit,
  lastSentMessage = '',
  placeholder = 'Kirjoita viesti...',
  maxLength = 500,
  mentionCandidates = [],
  kuiskaaCandidates = [],
  currentUser = null,
  replyingTo = null,
  onCancelReply,
  leftSlot,
}) {
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionIndex, setMentionIndex] = useState(-1);

  const [showKuiskaa, setShowKuiskaa] = useState(false);
  const [kuiskaaQuery, setKuiskaaQuery] = useState('');
  const [kuiskaaIndex, setKuiskaaIndex] = useState(-1);

  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerRef = useRef(null);
  const emojiBtnRef = useRef(null);
  const inputModuleRef = useRef(null);

  /** Keep fixed chat bar above the on-screen keyboard (mobile / visualViewport). */
  useLayoutEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return;

    const moduleEl = inputModuleRef.current;

    const syncBottom = () => {
      const el = inputModuleRef.current;
      if (!el) return;
      if (window.getComputedStyle(el).position !== 'fixed') {
        el.style.removeProperty('bottom');
        return;
      }
      const overlap = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      el.style.bottom = `${overlap}px`;
    };

    syncBottom();
    vv.addEventListener('resize', syncBottom);
    vv.addEventListener('scroll', syncBottom);
    return () => {
      vv.removeEventListener('resize', syncBottom);
      vv.removeEventListener('scroll', syncBottom);
      moduleEl?.style.removeProperty('bottom');
    };
  }, []);

  const currentUserId = currentUser?.id != null ? String(currentUser.id) : null;
  const mentionSuggestions = useMemo(() => {
    const q = mentionQuery.trim().toLowerCase();
    if (!q) return [];
    return mentionCandidates
      .filter((name) => typeof name === 'string')
      .filter((name) => name.toLowerCase().includes(q))
      .slice(0, 8);
  }, [mentionCandidates, mentionQuery]);

  const kuiskaaSuggestions = useMemo(() => {
    const q = kuiskaaQuery.trim().toLowerCase();
    const list = kuiskaaCandidates
      .filter((p) => p.userId !== currentUserId && String(p.userId) !== currentUserId)
      .map((p) => ({ ...p, displayLabel: getDisplayName(p) }));
    const limit = q ? 8 : 24;
    if (!q) return list.slice(0, limit);
    return list
      .filter((p) => {
        const u = (p.username || '').toLowerCase();
        const d = (p.displayName || '').toLowerCase();
        const lbl = (p.displayLabel || '').toLowerCase();
        return u.includes(q) || d.includes(q) || lbl.includes(q);
      })
      .slice(0, limit);
  }, [kuiskaaCandidates, kuiskaaQuery, currentUserId]);

  const showPopup = showMentions || showKuiskaa;
  const popupSuggestions = showMentions ? mentionSuggestions : kuiskaaSuggestions;
  const popupIndex = showMentions ? mentionIndex : kuiskaaIndex;

  const handleChange = (e) => {
    const text = e.target.value;
    onChangeValue(text);

    const trimmed = text.trimStart();
    const kuiskaaPrefix = '/kuiskaa ';
    if (trimmed.toLowerCase().startsWith(kuiskaaPrefix)) {
      const afterPrefix = trimmed.slice(kuiskaaPrefix.length);
      const firstWord = (afterPrefix.split(/\s/)[0] || '').trim();
      setShowKuiskaa(true);
      setKuiskaaQuery(firstWord);
      setKuiskaaIndex(-1);
      setShowMentions(false);
      setMentionQuery('');
      setMentionIndex(-1);
    } else {
      setShowKuiskaa(false);
      setKuiskaaQuery('');
      setKuiskaaIndex(-1);
      const lastSpace = text.lastIndexOf(' ');
      const fragment = text.slice(lastSpace + 1);
      if (fragment.startsWith('@') && fragment.length > 1) {
        setShowMentions(true);
        setMentionQuery(fragment.slice(1));
        setMentionIndex(-1);
      } else {
        setShowMentions(false);
        setMentionQuery('');
        setMentionIndex(-1);
      }
    }
  };

  const applyMention = (name) => {
    const text = value || '';
    const lastSpace = text.lastIndexOf(' ');
    const prefix = lastSpace === -1 ? '' : text.slice(0, lastSpace + 1);
    const completed = `${prefix}@${name} `;
    onChangeValue(completed);
    setShowMentions(false);
    setMentionQuery('');
    setMentionIndex(-1);
    if (inputRef && inputRef.current) {
      const el = inputRef.current;
      el.focus();
      const len = completed.length;
      try {
        el.setSelectionRange(len, len);
      } catch {
        // some inputs may not support setSelectionRange; safe to ignore
      }
    }
  };

  const applyKuiskaa = (selected) => {
    const text = value || '';
    const displayLabel = getDisplayName(selected);
    const prefix = '/kuiskaa ';
    const rest = text.trimStart().slice(prefix.length);
    const firstWordEnd = rest.search(/\s/);
    const suffix = firstWordEnd >= 0 ? rest.slice(firstWordEnd).trimStart() : '';
    const completed = suffix ? `${prefix}${displayLabel} ${suffix}` : `${prefix}${displayLabel} `;
    const beforeLen = text.length - text.trimStart().length;
    const completedWithLeading = text.slice(0, beforeLen) + completed;
    onChangeValue(completedWithLeading);
    setShowKuiskaa(false);
    setKuiskaaQuery('');
    setKuiskaaIndex(-1);
    if (inputRef && inputRef.current) {
      const el = inputRef.current;
      const len = completedWithLeading.length;
      el.focus();
      requestAnimationFrame(() => {
        try {
          el.setSelectionRange(len, len);
        } catch {}
      });
    }
  };

  const onEmojiClick = useCallback(
    (emojiData) => {
      const next = (value || '') + (emojiData.emoji || '');
      onChangeValue(next);
      setShowEmojiPicker(false);
      if (inputRef?.current) {
        inputRef.current.focus();
      }
    },
    [value, onChangeValue, inputRef]
  );

  useEffect(() => {
    if (!showEmojiPicker) return;
    const handleClickOutside = (e) => {
      if (
        emojiPickerRef.current?.contains(e.target) ||
        emojiBtnRef.current?.contains(e.target)
      )
        return;
      setShowEmojiPicker(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);

  const handleKeyDown = (e) => {
    if (showPopup && popupSuggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const setter = showMentions ? setMentionIndex : setKuiskaaIndex;
        setter((prev) => {
          const next = prev + 1;
          if (next >= popupSuggestions.length) return 0;
          return next;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const setter = showMentions ? setMentionIndex : setKuiskaaIndex;
        setter((prev) => {
          if (prev === -1) return popupSuggestions.length - 1;
          const next = prev - 1;
          if (next < 0) return popupSuggestions.length - 1;
          return next;
        });
      } else if (e.key === 'Enter') {
        if (popupIndex >= 0 && popupIndex < popupSuggestions.length) {
          e.preventDefault();
          if (showMentions) {
            applyMention(popupSuggestions[popupIndex]);
          } else {
            applyKuiskaa(popupSuggestions[popupIndex]);
          }
        }
      } else if (e.key === 'Escape') {
        setShowMentions(false);
        setMentionIndex(-1);
        setShowKuiskaa(false);
        setKuiskaaIndex(-1);
      }
      return;
    }
    if (e.key === 'ArrowUp' && (value || '').trim() === '' && lastSentMessage) {
      e.preventDefault();
      onChangeValue(lastSentMessage);
    }
    if (e.key === 'Escape' && showEmojiPicker) {
      setShowEmojiPicker(false);
    }
  };

  return (
    <div className="Chat-input-module" ref={inputModuleRef}>
      {replyingTo && (
        <div className="Chat-replying-to-hint">
          <span className="Chat-replying-to-text">
            Replying to: {shortenPreview(replyingTo.text)}
          </span>
          {onCancelReply && (
            <button
              type="button"
              className="Chat-replying-to-cancel"
              onClick={onCancelReply}
              aria-label="Peruuta vastaus"
            >
              Cancel
            </button>
          )}
        </div>
      )}
      <form className="Chat-input-row" onSubmit={onSubmit}>
        {leftSlot && <div className="Chat-input-left-slot">{leftSlot}</div>}
        <div className="Chat-input-wrapper">
          <input
            ref={inputRef}
            type="text"
            className="Chat-input"
            placeholder={placeholder}
            maxLength={maxLength}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
          />
          <div className="Chat-emoji-btn-wrap" ref={emojiPickerRef}>
            <button
              ref={emojiBtnRef}
              type="button"
              className={'Chat-emoji-btn' + (showEmojiPicker ? ' Chat-emoji-btn--open' : '')}
              onClick={() => setShowEmojiPicker((v) => !v)}
              aria-label="Valitse emoji"
              aria-expanded={showEmojiPicker}
            >
              <span className="Chat-emoji-btn-icon" aria-hidden>😊</span>
            </button>
            {showEmojiPicker && (
              <div className="Chat-emoji-picker-wrap">
                <EmojiPicker
                  theme="dark"
                  emojiStyle="facebook"
                  onEmojiClick={onEmojiClick}
                  width="100%"
                  height={320}
                  searchDisabled={false}
                  previewConfig={{ showPreview: false }}
                />
              </div>
            )}
          </div>
          {showPopup && popupSuggestions.length > 0 && (
            <div className="Chat-mention-popup">
              {(showMentions ? mentionSuggestions : kuiskaaSuggestions).map((item, index) => {
                const label = showMentions ? item : item.displayLabel;
                const isActive = index === popupIndex;
                return (
                  <button
                    key={showMentions ? label : item.userId + '-' + label}
                    type="button"
                    className={
                      'Chat-mention-item' +
                      (isActive ? ' Chat-mention-item--active' : '')
                    }
                    onClick={() =>
                      showMentions ? applyMention(item) : applyKuiskaa(item)
                    }
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <button type="submit" className="Button Chat-send">
          < Send />
        </button>
      </form>
    </div>
  );
}

export default ChatInput;
